import React, { useState } from 'react';
import { useAdCampaigns } from '../hooks/useAdCampaigns';
import { campaignProblem, campaignStatus, maxDailyBudget, money } from '../utils/ads';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';
const input = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none';
const EMPTY = { name: '', textId: '', imageId: '', headline: '', budget: '', city: '', radius: '20', ageMin: '18', ageMax: '65' };
const short = (t, n = 70) => (String(t || '').length > n ? `${String(t).slice(0, n)}…` : String(t || ''));

// Campañas de clic a WhatsApp: el borrador se arma con un texto y una imagen de «Contenido», se aprueba y n8n lo crea en Meta EN PAUSA.
// Activarlo (para que empiece a gastar) es un paso aparte que se hace en Meta.
export default function AdCampaigns({ connected, limits }) {
  const { campaigns, texts, images, missing, busy, create, approve, cancel } = useAdCampaigns(connected);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const problem = campaignProblem(form, limits);
  const max = maxDailyBudget(limits);

  if (!connected) return null;
  if (missing) return <div className="text-xs text-amber-300">Falta ejecutar la migración <code>20261028a_ads_campaigns.sql</code> en Supabase para crear campañas.</div>;

  const submit = async (e) => {
    e.preventDefault();
    if (problem) return;
    if (await create(form)) { setForm(EMPTY); setOpen(false); }
  };

  return (
    <section className={card} aria-label="Campañas">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-black text-seryi-heading">Campañas</h3>
        {!open && <button type="button" onClick={() => setOpen(true)} className="bg-seryi-green text-black text-xs font-extrabold px-4 py-2 rounded-xl">Crear campaña</button>}
      </div>
      <p className="text-[11px] text-seryi-text">Una campaña lleva a quien ve el anuncio directo a tu WhatsApp. Se crea siempre <strong>en pausa</strong>: no gasta nada hasta que tú la actives en Meta.</p>

      {open && (
        <form onSubmit={submit} className="space-y-3 bg-seryi-bg-dark rounded-2xl p-4" aria-label="Nueva campaña">
          <label className="block text-[11px] text-seryi-text">Nombre
            <input className={input} value={form.name} onChange={set('name')} maxLength={80} placeholder="Corte 2x1 octubre" />
          </label>
          <label className="block text-[11px] text-seryi-text">Texto del anuncio
            <select className={input} value={form.textId} onChange={set('textId')}>
              <option value="">{texts.length ? 'Elige un texto' : 'Primero genera un texto de anuncio en Contenido'}</option>
              {texts.map((t) => <option key={t.id} value={t.id}>{short(t.subject, 40)} — {short(t.result)}</option>)}
            </select>
          </label>
          <label className="block text-[11px] text-seryi-text">Imagen
            <select className={input} value={form.imageId} onChange={set('imageId')}>
              <option value="">{images.length ? 'Elige una imagen' : 'Primero genera una imagen en Contenido'}</option>
              {images.map((i) => <option key={i.id} value={i.id}>{short(i.subject, 60)}</option>)}
            </select>
          </label>
          <label className="block text-[11px] text-seryi-text">Titular (opcional, hasta 40 letras)
            <input className={input} value={form.headline} onChange={set('headline')} maxLength={40} />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="block text-[11px] text-seryi-text">Presupuesto por día (COP)
              <input className={input} inputMode="numeric" value={form.budget} onChange={set('budget')} placeholder={String(Math.min(10000, max))} />
            </label>
            <label className="block text-[11px] text-seryi-text">Ciudad
              <input className={input} value={form.city} onChange={set('city')} maxLength={80} />
            </label>
            <label className="block text-[11px] text-seryi-text">Radio (km)
              <input className={input} inputMode="numeric" value={form.radius} onChange={set('radius')} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-[11px] text-seryi-text">Edad desde<input className={input} inputMode="numeric" value={form.ageMin} onChange={set('ageMin')} /></label>
              <label className="block text-[11px] text-seryi-text">hasta<input className={input} inputMode="numeric" value={form.ageMax} onChange={set('ageMax')} /></label>
            </div>
          </div>
          <p className="text-[10px] text-seryi-text">Máximo por día para tu negocio: {money(max)}.</p>
          {problem && form.name && <p role="alert" className="text-[11px] text-amber-300">{problem}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !!problem} className="bg-seryi-green text-black text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-40">Guardar borrador</button>
            <button type="button" onClick={() => { setOpen(false); setForm(EMPTY); }} className="text-xs text-seryi-text px-3 py-2">Cancelar</button>
          </div>
        </form>
      )}

      {campaigns.length === 0 ? (
        <p className="text-xs text-seryi-text">Todavía no has creado campañas desde Keyla.</p>
      ) : (
        <ul className="space-y-2">
          {campaigns.map((c) => {
            const st = campaignStatus(c.status);
            return (
              <li key={c.id} className="bg-seryi-bg-dark rounded-xl p-3 space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-bold text-seryi-heading">{c.name}</p>
                  <span className={`text-[11px] font-bold ${st.tone}`}>{st.label}</span>
                </div>
                <p className="text-[11px] text-seryi-text">{money(c.daily_budget)} por día · {c.city} ({c.radius_km} km) · {c.age_min} a {c.age_max} años</p>
                {c.status === 'error' && c.error ? <p role="alert" className="text-[11px] text-rose-300">{c.error}</p> : st.note ? <p className="text-[11px] text-seryi-text">{st.note}</p> : null}
                {(c.status === 'borrador' || (c.status === 'error' && !c.meta_campaign_id)) && (
                  <div className="flex gap-2 pt-1">
                    <button type="button" disabled={busy} onClick={() => approve(c.id)} className="bg-seryi-green text-black text-[11px] font-extrabold px-3 py-1.5 rounded-lg disabled:opacity-40">Aprobar y crear en pausa</button>
                    <button type="button" disabled={busy} onClick={() => cancel(c.id)} className="text-[11px] text-seryi-text px-2">Descartar</button>
                  </div>
                )}
                {c.status === 'por_publicar' && <button type="button" disabled={busy} onClick={() => cancel(c.id)} className="text-[11px] text-seryi-text">Descartar</button>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
